'use client';

import { usePathname, useRouter } from 'next/navigation';
import { useLocale as useNextIntlLocale, useTranslations } from 'next-intl';
import { defaultLocale, type Locale } from './config';

/**
 * Hook to get current locale in client components
 */
export function useLocale() {
  return useNextIntlLocale() as Locale;
}

/**
 * Like `useLocale`, but yields `defaultLocale` when rendered outside a
 * NextIntlClientProvider instead of throwing. For plumbing (e.g. the CiC
 * client's locale) that must not take the page down when no provider exists.
 */
export function useLocaleSafe(): Locale {
  try {
    // biome-ignore lint/correctness/useHookAtTopLevel: the hook is called unconditionally on every render; try/catch only handles the missing-provider throw and never changes hook order
    return useNextIntlLocale() as Locale;
  } catch {
    return defaultLocale;
  }
}

/**
 * Hook to change locale in client components
 * This will navigate to the same page but with a different locale in the URL
 */
export function useChangeLocale() {
  const router = useRouter();
  const pathname = usePathname();

  return (locale: Locale) => {
    // Extract current locale from pathname (e.g., /en/... or /pt/...)
    const segments = pathname.split('/').filter(Boolean);

    // Replace the first segment (locale) with the new locale
    if (segments.length > 0 && ['en', 'pt', 'es'].includes(segments[0])) {
      segments[0] = locale;
      const newPath = `/${segments.join('/')}`;
      router.push(newPath);
    } else {
      // If no locale in path, prepend it
      router.push(`/${locale}${pathname}`);
    }
  };
}

/**
 * Hook to get translations in client components
 * Usage: const t = useTranslations('common')
 * Then: t('loading'), t('error'), etc.
 */
export { useTranslations, useTranslations as useTranslation };
